import { ref } from 'vue'
import { storefrontOffer, type MallImageShows, type MallSellUnit, type MallShippingCharge } from '@/utils/mallSell'

export type MallCategory = '위생용품' | '의료/요양' | '식품/건강' | '생활용품'

export interface MallHighlight {
  label: string
  value: string
}

export interface MallReview {
  id: string
  author: string
  org: string
  rating: number
  date: string
  content: string
  image?: string
  reply?: string
  repliedAt?: string
}

export interface MallQna {
  id: string
  author: string
  org: string
  secret: boolean
  question: string
  answer: string
  answeredAt: string
  date: string
}

/** 관리자가 검수하면서 붙이는 판매 옵션 */
export interface MallProductOption {
  group: string
  name: string
  /** 판매가에 더해지는 금액 */
  extra: number
  stock: number
  use: boolean
  /** 쇼핑몰 목록·상품 화면에 먼저 보여줄 구성 */
  front?: boolean
}

/** 상세 페이지 본문 한 덩어리. 판매업체가 글과 이미지를 순서대로 쌓아 만든다. */
export interface MallDetailBlock {
  type: 'HEADING' | 'TEXT' | 'IMAGE' | 'NOTICE'
  text: string
  imageUrl: string
  alt: string
}

export interface MallProduct {
  id: string
  name: string
  category: MallCategory
  summary: string
  description: string
  image: string
  images?: string[]
  listPrice: number
  price: number
  unit: string
  badge?: string
  tags: string[]
  highlights: MallHighlight[]
  rating: number
  reviewCount: number
  soldCount: number
  freeShipping: boolean
  minOrder: number
  orderStep?: number
  maxOrder?: number
  /** REGULAR 상시몰 · SALE 공동구매몰 */
  mallKind?: 'REGULAR' | 'SALE'
  sellUnit?: MallSellUnit
  imageShows?: MallImageShows
  packQty?: number
  shippingCharge?: MallShippingCharge
  shippingEvery?: number
  reviews: MallReview[]
  qnas?: MallQna[]

  /* ---- 판매업체가 직접 올린 상품에만 채워지는 값 ---- */
  /** 판매업체 코드 (스토어 페이지) */
  sellerCode?: string
  /** 판매업체 상호 */
  storeName?: string
  /** 판매업체 당일 출고 마감 (예: 14:00) */
  storeCutoff?: string
  /** 판매업체 교환·반품 안내 */
  storeReturnPolicy?: string
  /** 판매업체 반품 배송비 (예전 스토어 공통값) */
  storeReturnFee?: number
  /** 이 상품의 환불 배송비 기준 수량 */
  returnEvery?: number
  /** 이 상품의 환불 배송비. 기준 수량마다 받는 금액 */
  returnFee?: number
  /** 상세 페이지 본문 블록 */
  blocks?: MallDetailBlock[]
  /** 관리자가 붙인 판매 옵션 */
  options?: MallProductOption[]
  /** 목록 응답에 실리는 전면 구성. 상세는 options[].front 를 쓴다 */
  frontOptionName?: string
  frontOptionExtra?: number
  stock?: number
  soldOut?: boolean
  leadTime?: string
  shippingFee?: number
  origin?: string
  maker?: string
  /** 관리자가 지정한 이달의 특가 */
  isDeal?: boolean
  dealSort?: number
  dealStartAt?: string | null
  dealEndAt?: string | null
  /** 관리자가 지정한 관련·대체 상품 코드 */
  relatedCodes?: string[]
  substituteCodes?: string[]
}

export const MALL_CATEGORIES: Array<'전체' | MallCategory> = [
  '전체',
  '위생용품',
  '의료/요양',
  '식품/건강',
  '생활용품',
]

export const MALL_CATEGORY_META: Array<{
  id: '전체' | MallCategory
  label: string
  icon: string
}> = [
  { id: '전체', label: '전체', icon: 'mdi-view-grid-outline' },
  { id: '위생용품', label: '위생용품', icon: 'mdi-hand-wash-outline' },
  { id: '의료/요양', label: '의료·요양', icon: 'mdi-medical-bag' },
  { id: '식품/건강', label: '식품·건강', icon: 'mdi-food-apple-outline' },
  { id: '생활용품', label: '생활용품', icon: 'mdi-sofa-outline' },
]

export type MallSortKey = 'recommend' | 'priceAsc' | 'priceDesc' | 'popular' | 'review'

/** 몰 목록에 정렬 값이 없을 때의 기본. 판매많은순. */
export const MALL_DEFAULT_SORT: MallSortKey = 'popular'

export const MALL_SORT_OPTIONS: Array<{ key: MallSortKey; label: string }> = [
  { key: 'popular', label: '판매많은순' },
  { key: 'recommend', label: '추천순' },
  { key: 'review', label: '후기많은순' },
  { key: 'priceAsc', label: '낮은가격순' },
  { key: 'priceDesc', label: '높은가격순' },
]

/**
 * 판매업체가 등록해 관리자 승인까지 끝난 실제 상품.
 *
 * 장바구니 · 찜 · 검색처럼 상품을 동기로 찾아 쓰는 화면이 많아서,
 * 목록을 한 번 받아 여기에 담아 둔다. 목록이 늦게 도착해도 화면이 다시 그려지도록
 * 반응형 ref 로 둔다. 평범한 배열이면 나중에 채워도 computed 가 갱신되지 않는다.
 */
const liveProducts = ref<MallProduct[]>([])

export function setLiveProducts(products: MallProduct[]) {
  liveProducts.value = products
}

export function upsertLiveProduct(product: MallProduct) {
  const index = liveProducts.value.findIndex((item) => item.id === product.id)
  if (index >= 0) {
    liveProducts.value = liveProducts.value.map((item) => (item.id === product.id ? product : item))
    return
  }
  liveProducts.value = [product, ...liveProducts.value]
}

/** 몰에서 내려간 상품은 캐시에서도 빼야 목록·검색에 남지 않는다. */
export function removeLiveProduct(id: string) {
  liveProducts.value = liveProducts.value.filter((item) => item.id !== id)
}

/** 판매·승인된 실제 상품만 노출한다. */
export function getAllProducts(): MallProduct[] {
  return [...liveProducts.value]
}

export function getProductById(id: string): MallProduct | undefined {
  return getAllProducts().find((product) => product.id === id)
}

export function formatPrice(value: number): string {
  return value.toLocaleString('ko-KR')
}

/** 품절은 어떤 정렬이든 목록 맨 뒤로 보낸다. */
function soldOutRank(product: MallProduct): number {
  return product.soldOut ? 1 : 0
}

export function sortProducts(products: MallProduct[], sort: MallSortKey): MallProduct[] {
  const list = [...products]
  const by = (compare: (a: MallProduct, b: MallProduct) => number) =>
    list.sort((a, b) => soldOutRank(a) - soldOutRank(b) || compare(a, b))

  switch (sort) {
    case 'priceAsc':
      return by((a, b) => storefrontOffer(a).price - storefrontOffer(b).price)
    case 'priceDesc':
      return by((a, b) => storefrontOffer(b).price - storefrontOffer(a).price)
    case 'popular':
      return by((a, b) => b.soldCount - a.soldCount)
    case 'review':
      return by((a, b) => b.reviewCount - a.reviewCount)
    case 'recommend':
    default:
      return by((a, b) => b.rating * b.reviewCount - a.rating * a.reviewCount)
  }
}

function isDealActive(product: MallProduct, now = Date.now()): boolean {
  if (!product.isDeal) return false
  if (product.dealStartAt) {
    const start = new Date(product.dealStartAt).getTime()
    if (Number.isFinite(start) && now < start) return false
  }
  if (product.dealEndAt) {
    const end = new Date(product.dealEndAt).getTime()
    if (Number.isFinite(end) && now > end) return false
  }
  return true
}

/**
 * 몰 홈 특가 영역.
 * 관리자가 지정한 상품이 있으면 그 순서를 쓰고, 없으면 판매많은순으로 자동 선정한다.
 * 기간이 지난 특가는 제외한다.
 */
export function getDealProducts(limit = 6): MallProduct[] {
  const now = Date.now()
  const curated = getAllProducts()
    .filter((product) => isDealActive(product, now))
    .sort((a, b) => (a.dealSort ?? 0) - (b.dealSort ?? 0) || b.soldCount - a.soldCount)

  if (curated.length) {
    return curated.slice(0, limit)
  }

  return sortProducts(getAllProducts(), 'popular').slice(0, limit)
}

export function getBestProducts(limit = 8): MallProduct[] {
  return sortProducts(getAllProducts(), 'popular').slice(0, limit)
}

/** 신상품 — NEW 배지 우선, 없으면 최신 등록 순(목록 앞쪽) */
export function getNewProducts(limit = 8): MallProduct[] {
  const all = getAllProducts()
  const news = all.filter((product) => String(product.badge || '').toUpperCase() === 'NEW')
  if (news.length) return news.slice(0, limit)
  return all.slice(0, limit)
}

export function getRecommendProducts(limit = 8): MallProduct[] {
  return sortProducts(getAllProducts(), 'recommend').slice(0, limit)
}
